import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-serif-display text-[color:var(--parchment)] text-2xl md:text-3xl tracking-tight mb-3 constellation-underline">
      {title}
    </h2>
    <div className="text-[color:var(--parchment-soft)] font-sans-body leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

export function LegalLayout({ title, updated, children }) {
  return (
    <div className="min-h-screen relative z-10">
      <header className="border-b border-[color:var(--gold)]/15">
        <div className="mx-auto max-w-[900px] px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/almanac" className="label-overline hover:text-[color:var(--gold)]">← Back to Guidance</Link>
          <span className="font-serif-italic text-[color:var(--text-secondary)] text-sm">Earth · Pulse</span>
        </div>
      </header>
      <main className="mx-auto max-w-[900px] px-6 md:px-10 py-14 md:py-20">
        <div className="label-overline mb-3">Legal</div>
        <h1 className="font-serif-display text-4xl md:text-5xl text-[color:var(--parchment)] tracking-tight mb-2">{title}</h1>
        <p className="font-serif-italic text-[color:var(--text-secondary)] mb-12">Last updated: {updated}</p>
        {children}
      </main>
    </div>
  );
}

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="22 July 2026">
      <Section title="Who we are">
        <p>
          Earth · Pulse (&quot;we&quot;, &quot;us&quot;) is a personal wellness and space-weather guidance app operated in the United Kingdom.
          For any privacy questions contact us at <a href="mailto:privacy@earthpulse.store" className="text-[color:var(--gold)] underline">privacy@earthpulse.store</a>.
          We act as data controller for the small amount of personal data described below.
        </p>
      </Section>
      <Section title="What we collect">
        <p><strong className="text-[color:var(--parchment)]">Journal entries.</strong> If you log mood/energy/clarity entries, we store them anonymously (no account required) alongside the current cosmic readings. Entries live only in our database and are not sold, shared, or profiled.</p>
        <p><strong className="text-[color:var(--parchment)]">Birth-chart purchases.</strong> If you buy a Cosmic Birth Chart Reading, we store the birth date, optional birth time, optional place/lat-long, optional name, plus the Stripe checkout session ID, so we can produce your reading. We keep this for 6 years to meet UK accounting law and then delete it.</p>
        <p><strong className="text-[color:var(--parchment)]">Payment data.</strong> Card details never touch our servers — Stripe processes them directly. Stripe's own privacy policy: <a className="text-[color:var(--gold)] underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">stripe.com/privacy</a>.</p>
        <p><strong className="text-[color:var(--parchment)]">Technical logs.</strong> We keep short-lived server logs (IP address, request path, timestamp) for up to 14 days for abuse detection.</p>
        <p><strong className="text-[color:var(--parchment)]">Cookies.</strong> We use only essential cookies (session, CSRF). No advertising or analytics cookies are set unless you explicitly accept them.</p>
      </Section>
      <Section title="Legal bases (UK GDPR)">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Contract:</strong> processing your birth-chart reading is necessary to deliver the paid product.</li>
          <li><strong>Legitimate interest:</strong> keeping short-lived server logs to protect the service.</li>
          <li><strong>Consent:</strong> any non-essential analytics/marketing cookies, only if you tick &quot;Accept&quot; on the cookie banner.</li>
          <li><strong>Legal obligation:</strong> keeping payment records for 6 years under UK tax law.</li>
        </ul>
      </Section>
      <Section title="Third parties (data processors)">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Stripe</strong> — payment processing (USA/Ireland, standard contractual clauses).</li>
          <li><strong>Anthropic (Claude)</strong> — AI narrative generation for daily briefings and readings (USA, standard contractual clauses). We send only the birth-chart placements and, if you journal, your journal aggregates — never your name or email.</li>
          <li><strong>NOAA SWPC &amp; ResonanceOne</strong> — public space-weather feeds we read from. No personal data is sent to them.</li>
          <li><strong>OpenStreetMap (Nominatim)</strong> — birthplace autocomplete. Only the text you type is sent, no personal identifiers.</li>
          <li><strong>Emergent</strong> — our hosting provider.</li>
        </ul>
      </Section>
      <Section title="Your rights">
        <p>You can, free of charge and at any time, ask us to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Access a copy of any personal data we hold on you</li>
          <li>Correct anything that&apos;s wrong</li>
          <li>Erase your data (right to be forgotten) — subject to legal retention above</li>
          <li>Object to processing, or ask us to restrict it</li>
          <li>Port your data to another provider (machine-readable JSON)</li>
        </ul>
        <p>Email <a className="text-[color:var(--gold)] underline" href="mailto:privacy@earthpulse.store">privacy@earthpulse.store</a> with your request — we respond within 30 days. You also have the right to complain to the UK regulator (<a className="text-[color:var(--gold)] underline" href="https://ico.org.uk" target="_blank" rel="noreferrer">ico.org.uk</a>).</p>
      </Section>
      <Section title="International transfers">
        <p>Some processors (Stripe, Anthropic) are based in the USA. All transfers rely on the UK International Data Transfer Addendum and EU Standard Contractual Clauses.</p>
      </Section>
      <Section title="Children">
        <p>Earth · Pulse is not directed at children under 16 and we do not knowingly collect their data.</p>
      </Section>
      <Section title="Changes">
        <p>We may update this policy from time to time. The &quot;last updated&quot; date at the top reflects the latest change.</p>
      </Section>
    </LegalLayout>
  );
}
